import { Tooltip } from '@civilgrid/ui/components';
import type { Table } from '@tanstack/react-table';
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { Prefs } from '~/common/prefs';
import { useQuery } from '~/common/routerUtils';
import { NotificationBadge } from '~/components2/Badge';
import {
  type AdvancedFilterColumnDef,
  FilterOperator,
  type FilterProperties,
  Shortcuts,
  type TShortcut,
} from './common';
import type { FilterGroup } from './Filters';
import Group from './Filters/Group';
import Button from './Legacy/LegacyButton';
import Popover, { PopoverContent } from './Legacy/LegacyPopover';
import store from './store';

export * from './common';
export * from './Filters';
export * from './filterFn';

export interface AdvancedFilterV2Props<T> {
  id: string;
  table: Table<T>;
  columns: AdvancedFilterColumnDef[];
  shortcuts?: TShortcut[];
  defaultFilterState?: FilterProperties[];
  onQuery?: (filterTree: FilterGroup, searchParams: { [key: string]: any }) => void;
}

export default function AdvancedFilterV2<T>({
  id,
  table,
  columns = [],
  shortcuts = [],
  defaultFilterState = [],
  onQuery,
}: AdvancedFilterV2Props<T>) {
  const { filterTree } = useSnapshot(store) as typeof store;
  const query = useQuery();
  const filterParams = query.get('filter-state');
  const filterForUserWhoClicked = query.get('filter-user');

  const dispatchQuery = () => {
    const t = store.filterTree;
    onQuery?.(t, store.getFiltersAsSearchParams(t));
  };

  // for window close events
  useEffect(() => {
    const persistOnWindowClose = () => {
      store.persistFilters(id);
    };

    window.addEventListener('beforeunload', persistOnWindowClose);

    return () => {
      window.removeEventListener('beforeunload', persistOnWindowClose);
    };
  }, [id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const initializeFilters = async () => {
      if (filterForUserWhoClicked) {
        await store.getMe();
      }

      let defaultFilters: FilterProperties[] = [];

      const storedFilters = sessionStorage.getItem(`${Prefs.ADVANCED_FILTER_STATE}.${id}`);

      // first check if filter query exists
      if (filterParams) {
        try {
          const parsed: FilterProperties[] = JSON.parse(filterParams);

          if (filterForUserWhoClicked) {
            if (parsed[0] && 'children' in parsed[0]) {
              parsed[0].children?.push({
                id: 'assignedTo.firstName',
                columnId: 'assignedTo.firstName',
                value: store.me?.firstName as string,
                operator: FilterOperator.Includes,
                type: 'rule',
              });
            }
          }

          if (parsed?.[0]?.id) {
            defaultFilters = parsed;
          }
        } catch (e) {
          console.error(`Error parsing filter-state query - ${filterParams}`, e);
        }
      } else if (storedFilters) {
        try {
          const parsed: FilterProperties[] = JSON.parse(storedFilters);
          if (parsed) {
            defaultFilters = parsed;
          }
        } catch (e) {
          console.error(`Error parsing store filters - ${storedFilters}`, e);
        }
      } else if (defaultFilterState) {
        defaultFilters = defaultFilterState;
      }

      if (defaultFilters && columns[0]) {
        store.buildFilterTreeFromProps(defaultFilters, 'root');

        // once tree is built, if an onQuery method is preset, invoke it
        dispatchQuery();
      }
    };

    initializeFilters();

    return () => {
      store.persistFilters(id);
      store.reset();
    };
  }, []);

  if (!columns[0]) return null;

  return (
    <Popover
      usePortal={true}
      side="left"
      align="start"
      trigger={
        <Tooltip asChild text="Filter table rows">
          <div className="relative">
            <Button icon="filter" ghost aria-label="Filter table rows" data-testid={`${id}-advanced-filter-trigger`} />
            <NotificationBadge visible={filterTree.hasChildren} />
          </div>
        </Tooltip>
      }
    >
      {() => {
        return (
          <PopoverContent className="relative max-h-[50vh] max-w-[65vw] overflow-auto text-xs">
            <div className="absolute right-4 top-4 z-20">
              <Button
                onClick={() => {
                  dispatchQuery();
                }}
              >
                Apply Filters
              </Button>
            </div>

            <Group table={table} columns={columns} group={filterTree} onQuery={onQuery} />

            <Shortcuts table={table} columns={columns} shortcuts={shortcuts} />
          </PopoverContent>
        );
      }}
    </Popover>
  );
}
